import React, {useId, useState} from "react";
import {FaHospitalUser} from "react-icons/fa";

/**
 * The portal's own sign-in page: the patient chooses their clinic, then signs in on its page (lib/clinicSignIn.js).
 * Nothing is chosen for them — a list that came with its first clinic chosen told patients who did not change it that
 * their password was wrong. `clinics`: null while they come, false when they did not come, else the list.
 */
export default function ClinicChooser({clinics, notice = null, onChoose, onRetry}) {
  const [chosen, setChosen] = useState("");
  const id = useId();
  const loading = clinics === null;
  const failed = clinics === false;

  return (
    <div className="w-full max-w-md">
      <form
        className="w-full border border-gray-100 bg-white/80 backdrop-blur-md shadow-2xl rounded-2xl p-10 sm:p-12"
        onSubmit={(event) => {
          event.preventDefault();
          if (chosen) onChoose(chosen);
        }}
      >
        <div className="mb-4 h-16 flex items-center justify-center">
          <FaHospitalUser className="text-[#6772e5]" size={44} aria-hidden="true" />
        </div>
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-slate-800">Choose your clinic</h2>
          <p className="text-sm text-slate-500">Then sign in with your email and password.</p>
        </div>
        {notice ? (
          <p role="alert" className="mb-4 rounded-xl border border-amber-200/70 bg-amber-50/80 px-4 py-2.5 text-sm text-amber-800">
            {notice}
          </p>
        ) : null}
        {failed ? (
          <div role="alert" className="text-sm text-slate-600 text-center">
            <p className="mb-3">We couldn't load the clinics just now.</p>
            <button
              type="button"
              onClick={onRetry}
              className="p-2.5 px-5 rounded-lg bg-[#6772e5] hover:bg-[#5b63d3] text-white font-medium text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6772e5]"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
              Your clinic
            </label>
            <select
              id={id}
              value={chosen}
              onChange={(event) => setChosen(event.target.value)}
              disabled={loading}
              className="input appearance-none w-full py-3 px-4 rounded-lg bg-white border border-gray-200 focus:border-[#6772e5] focus:ring-2 focus:ring-[#6772e5]/40 transition"
            >
              <option value="">{loading ? "Loading clinics…" : "Choose your clinic…"}</option>
              {(clinics || []).map((clinic) => (
                <option key={clinic.db} value={clinic.db}>
                  {clinic.name}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={!chosen}
              className="mt-6 w-full p-2.5 px-5 rounded-lg bg-[#6772e5] hover:bg-[#5b63d3] disabled:opacity-50 text-white font-medium text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6772e5]"
            >
              Continue
            </button>
          </>
        )}
        <p className="text-xs text-slate-500 mt-6 text-center">
          The link in your clinic's emails takes you straight to its sign-in page.
        </p>
      </form>
    </div>
  );
}
